import { useCallback, useEffect, useState } from 'react'
import { handlePhonePrefix, KEYS } from '@/methods/videoIdPhoneHandler'
import { axiosInstance } from '@/methods/axiosConfig'
import { devLog } from '@/methods/devLog'
import { API_URL } from '@/constants/apiUrl'
import { actionReport } from '@/methods/actionReport'

export const usePhoneHandler = () => {
  const [phoneHandled, setPhoneHandled] = useState(false)
  const [phone, setPhone] = useState('')
  const [otpFocusHandled, setOtpFocusHandled] = useState(false)

  const getPhone = async () => {
    try {
      const res = await axiosInstance.get(`${API_URL}/electronic-id/get-phone-info`, {
        withCredentials: true,
      })

      const { phoneNumber } = res.data
      setPhone(phoneNumber)
    } catch (e) {
      devLog(e)
    }
  }

  const handlePhoneNumber = () => {
    setPhoneHandled(true)

    const element = document.querySelector('.eid-textbox') as HTMLInputElement

    element.value = handlePhonePrefix(phone) || ''
    element.setAttribute('pattern', '\\d*')

    element.addEventListener('paste', function (event) {
      event.preventDefault()

      let pastedData = event?.clipboardData?.getData('text') ?? ''

      if (pastedData.charAt(0) === '0' || pastedData.charAt(0) === '+') {
        pastedData = handlePhonePrefix(pastedData.replace(/^[0+]+/, '+')) || ''
      }

      element.value = pastedData

      element.focus()
      element.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }))
    })

    element.addEventListener('keydown', function (event) {
      const key = event.key
      const ctrlOrCmd = event.ctrlKey || event.metaKey

      if (
        !/[0-9]/.test(key) &&
        !KEYS.includes(key) &&
        !(ctrlOrCmd && (key === 'c' || key === 'v' || key === 'a'))
      ) {
        event.preventDefault()
      }

      if (key === '0' && this.value.length === 0) {
        event.preventDefault()
      }
    })

    setTimeout(() => {
      element.focus()
      element.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }))
    }, 300)
  }

  const clearPhoneHandledListener = () => {
    setPhoneHandled(false)
  }

  const handleOtpFocus = () => {
    const shouldHandleFocus = !navigator.credentials

    if (shouldHandleFocus) {
      setOtpFocusHandled(true)
      return
    }

    const otpInput = document.getElementsByClassName(
      'eid-textbox-character'
    ) as HTMLCollectionOf<HTMLInputElement>

    if (otpInput.length > 3) {
      otpInput[0].focus()
      setOtpFocusHandled(true)
    }
  }

  const handlePhoneNumberChangeClick = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement | null
    if (!target) return

    const link = target.closest('.eid-phone-update > .eid-a')

    if (!link) return

    actionReport({
      type: 'event.onboarding-web.process.OTP_PHONE_NUMBER_CHANGE',
      payload: {},
    })

    clearPhoneHandledListener()
  }, [])

  useEffect(() => {
    document.addEventListener('click', handlePhoneNumberChangeClick)

    return () => {
      document.removeEventListener('click', handlePhoneNumberChangeClick)
    }
  }, [handlePhoneNumberChangeClick])

  useEffect(() => {
    window.addEventListener('online', clearPhoneHandledListener)

    return () => {
      window.removeEventListener('online', clearPhoneHandledListener)
    }
  }, [])

  return {
    getPhone,
    handlePhoneNumber,
    phoneHandled,
    handleOtpFocus,
    otpFocusHandled,
  }
}
